<template>
  <div>
    <div v-for="cmtid in Object.keys(allComments)"
         :key="cmtid"
         :class="`sideCommentContainer visible commentThread ${isCurrentThread(cmtid) ? 'current' : ''}`"
         :style="'top : ' + getTopPosition(allComments[cmtid])"
    >
      <CommentThread
        :focused="isCurrentThread(cmtid)"
        :comment-thread="allComments[cmtid].data"
        @comment-thread-closed="commentThreadClosed"
        @set-comment="setComment"
        @remove-comment="removeComment"
        @click="() => setCurrentThread(cmtid)"
      />
    </div>
    <div
      :class="`sideCommentContainer commentButton ${showCommentButton ? 'visible' : ''}`"
      :style="'top : ' + topPosition"
    >
      <v-btn v-if="showCommentButton" icon elevation="3" @click="addComment">
        <v-icon color="primary">
          add_comment
        </v-icon>
      </v-btn>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { posToDOMRect } from '@tiptap/core';
import { nanoid } from 'nanoid';
import { types } from '../../../../../store/rootTypes';
import CommentThread from './CommentThread';

export const getEmptyCommentThread = () => ({
  id: nanoid(),
  comments: [],
});

export default {
  name: 'SideComment',
  components: {
    CommentThread,
  },
  props: {
    editor: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    showCommentButton: false,
    showNewCommentThread: true,
    editorHasSelection: false,
    currentThreadId: undefined,
    allComments: {},
    topPosition: '100px',
  }),
  mounted() {
    this.editor.on('selectionUpdate', this.selectionUpdate);
    this.editor.on('update', this.editorUpdate);
    this.$el.offsetParent.addEventListener('scroll', this.onEditorScroll);
    this.refreshAllComments();
  },
  computed: {
    ...mapGetters({
      currentUserId: types.authentication.getters.userId,
    }),
    currentToolClass() {
      if (this.showCommentButton) {
        return 'commentButton';
      } if (this.showNewCommentThread) {
        return 'commentThread';
      }

      return '';
    },
  },
  methods: {
    setComment({ commentThread, commentText }) {
      if (commentText && commentText.length > 0) {
        commentThread.comments.push({
          userId: this.currentUserId,
          timestamp: Date.now(),
          content: commentText,
        });
      }
      this.editor.chain().setComment(JSON.stringify(commentThread)).run();
    },
    removeComment() {
      this.editor.chain().unsetComment().run();
    },
    isCurrentThread(cmtid) {
      return cmtid === this.currentThreadId;
    },
    editorUpdate() {
      this.refreshAllComments();
    },
    refreshAllComments() {
      const commentSpans = Array.from(this.editor.view.dom.querySelectorAll('span[data-comment]'));
      const allComments = {};
      commentSpans.forEach(cspan => {
        const data = JSON.parse(cspan.getAttribute('data-comment'));
        // Marks on multiple paragraphs are set as an individual span on each paragraph
        // Here we only take the first span and use it to manage the comment data
        if (allComments[data.id] !== undefined) {
          return;
        }

        allComments[data.id] = {
          data,
          startPosition: this.editor.view.posAtDOM(cspan),
        };
      });

      this.allComments = allComments;
    },
    selectionUpdate() {
      // TODO maybe also handle editor losing focus
      this.editorHasSelection = !this.editor.state.selection.empty;

      if (this.editorHasSelection) {
        this.updatePosition();
        const selectionThread = this.findThreadForSelection();
        this.currentThreadId = selectionThread ? selectionThread.id : undefined;
        this.showCommentButton = !this.currentThreadId;
        this.showNewCommentThread = !this.currentThreadId && this.writingNewThread;
      } else {
        this.currentThreadId = undefined;
        this.showCommentButton = false;
        this.showNewCommentThread = false;
      }
    },
    findThreadForSelection() {
      /**
      * TODO this does not work exactly as intended :
      * When there is already a comment somewhere in the selection, the
      * new selection will override it and it will be lost
      *
      * Instead it should create a new comment that overlaps with the
      * existing comment but prosemirror marks don't work that way since
      * they are span. They have to be entirely contained within another
      * mark, partial overlaps don't seem to be supported
      */
      const markAttributes = this.editor.getAttributes(
        this.editor.schema.marks.comment,
      );
      if (markAttributes.comment === undefined) {
        return undefined;
      }

      // TODO SECURITY be careful about XSS
      return JSON.parse(markAttributes.comment);
    },
    updatePosition() {
      const positionStart = this.editor.state.selection.from;
      const position = posToDOMRect(this.editor.view, positionStart, positionStart);
      const topOffset = position.top - this.$el.offsetParent.getBoundingClientRect().top + this.$el.offsetParent.scrollTop;
      this.topPosition = `${topOffset}px`;
    },
    getCommentDomNode(comment) {
      let nodeAtCommentStart = this.editor.view.domAtPos(comment.startPosition, 1).node;
      if (nodeAtCommentStart.nodeType === Node.TEXT_NODE) {
        nodeAtCommentStart = nodeAtCommentStart.parentElement;
      }

      return nodeAtCommentStart;
    },
    getTopPosition(comment) {
      const nodeAtCommentStart = this.getCommentDomNode(comment);
      const topDistance = nodeAtCommentStart.getBoundingClientRect().y - this.$el.offsetParent.getBoundingClientRect().top;
      return `${topDistance}px`;
    },
    addComment() {
      this.showCommentButton = false;
      this.showCommentThread = true;
      const commentThread = getEmptyCommentThread();
      this.setComment({ commentThread });
      this.setCurrentThread(commentThread.id);
    },
    commentThreadClosed() {
      this.showCommentThread = false;
      this.showCommentButton = false;
      if (
        this.allComments[this.currentThreadId] === undefined
        || this.allComments[this.currentThreadId].data.comments.length === 0
      ) {
        this.removeComment();
      }

      this.currentThreadId = undefined;
      window.getSelection().removeAllRanges();
    },
    onEditorScroll() {
      this.updatePosition();
    },
    setCurrentThread(id) {
      if (this.currentThreadId === id) {
        return;
      }
      this.currentThreadId = id;
      const selection = window.getSelection();
      const range = document.createRange();
      // TODO getting the commentDomNode is not always enough :
      // if there is another mark that starts in the middle the selection
      // will end at the start of the next mark
      //
      // This is probably very easy to fix using the prosemirror comment mark
      // range and setSelection on it
      const threadNode = this.getCommentDomNode(this.allComments[id]);
      range.setStart(threadNode.firstChild, 0);
      range.setEnd(threadNode.firstChild, threadNode.firstChild.length);
      selection.removeAllRanges();
      selection.addRange(range);
    },
  },
};
</script>
<style lang="scss" scoped>
.sideCommentContainer {
  position: absolute;
  right: 0;
  background-color: white;
  border-radius: 100%;
  z-index: 1;
  opacity: 0;
  visibility: hidden;
  -webkit-transition: opacity 150ms, width 150ms, right 150ms;
  transition: opacity 150ms, width 150ms, right 150ms;

  &.commentThread {
    width: 285px;
    right: -290px;

    &.current {
      width: 320px;
      right: -270px;
      opacity: 1;
      z-index: 2;
    }
  }

  &.visible {
    visibility: visible;
    opacity: 1;
  }

}

</style>
